/**
 * Banner global de impersonación activa (Fase 3).
 *
 * Se monta en AppRoot (main.tsx) por encima de todas las rutas. Visible en
 * TODA la app cuando hay una sesión de impersonación activa.
 *
 * GOTCHA CRÍTICO: durante impersonación, getSession()/useSession() SIGUE
 * devolviendo al ADMIN (la cookie de sesión no cambia). El usuario efectivo
 * (target) SOLO se refleja en el worker. Por eso el banner consume
 * PlatformStatusContext (/impersonate/status), NUNCA useSession.
 *
 * Referencia: DESIGN_platform_console.md §3.1
 */
import { useState } from "react";
import { usePlatformStatus } from "../../lib/PlatformStatusContext";
import { stopImpersonation } from "../../lib/platform-api";

export function ImpersonationBanner() {
  const { impersonating, targetEmail, targetName, refetch } = usePlatformStatus();
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState<string | null>(null);

  // No renderizar si no hay impersonación activa
  if (!impersonating) return null;

  const displayName = targetName ?? targetEmail ?? "usuario desconocido";

  const handleStop = async () => {
    setStopping(true);
    setStopError(null);
    try {
      // POST /stop siempre da 200 (idempotente — sacado de requirePlatformSession).
      // Solo manejamos errores de red o del servidor inesperados.
      await stopImpersonation();
      refetch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al detener impersonación";
      setStopError(msg);
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="impersonation-banner" role="alert" aria-live="assertive">
      <span className="impersonation-banner-icon" aria-hidden="true">
        ⚠
      </span>
      <span className="impersonation-banner-msg">
        Estás actuando como <strong>{displayName}</strong>
      </span>
      {stopError && (
        <span className="impersonation-banner-error" role="alert">
          {stopError}
        </span>
      )}
      <button
        type="button"
        className="impersonation-banner-stop"
        onClick={() => void handleStop()}
        disabled={stopping}
        aria-label="Salir de la sesión de impersonación"
      >
        {stopping ? "Saliendo..." : "Salir de impersonación"}
      </button>
    </div>
  );
}
